import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { stockImages } from "@/data/site/stockImages";
import { PageSEO } from "@/components/site/PageSEO";
import { brand } from "@/data/site/brand";
import { Reveal } from "@/components/motion/Reveal";
import { SocialLinks } from "@/components/site/SocialLinks";
import { CaseReviewForm } from "@/components/site/CaseReviewForm";

const Ayuda = () => {


  return (
    <SiteLayout>
      <PageSEO
        title="Ayuda con el IRS — Consulta Gratis en Español"
        description={`¿Problemas con el IRS? Hable con un CPA de ${brand.legalName}. Revisión de caso gratis y confidencial en español. Llame al ${brand.phone}.`}
        path="/ayuda"
        image={stockImages.consultation.src}
      />

      <section className="relative bg-gradient-navy text-white overflow-hidden">
        <HeroBackground image={stockImages.consultation} priority />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">
              Hablamos español
            </p>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance]">
              ¿Listo para acabar con sus problemas con el IRS?{" "}
              <span className="text-gold">Estamos aquí para ayudarle.</span>
            </h1>
            <p className="text-lg text-white/80 max-w-2xl leading-relaxed">
              Llene el formulario y un CPA con licencia se comunicará con usted lo antes posible.
              Consulta gratis y confidencial — sin compromiso, sin presión de ventas.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-32 bg-white">
        <div className="container mx-auto px-4 max-w-5xl grid lg:grid-cols-[1fr_1.5fr] gap-16">
          <Reveal className="space-y-8" direction="left">
            <InfoRow icon={Phone} label="Teléfono" value={brand.phone} href={`tel:${brand.phoneTel}`} />
            <InfoRow icon={Mail} label="Correo electrónico" value={brand.email} href={`mailto:${brand.email}`} />
            <InfoRow icon={MapPin} label="Oficina" value={brand.address.full} />
            <InfoRow icon={Clock} label="Horario" value="Lun–Vie 9:00am–6:00pm CT" />
            <div className="rounded-lg border border-border bg-secondary/40 p-4">
              <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.15em] mb-2">
                ¿Necesita ayuda urgente?
              </div>
              <p className="text-sm text-navy leading-relaxed">
                Si está recibiendo cartas amenazantes del IRS, le están embargando el sueldo o
                congelando sus cuentas, llámenos directamente al{" "}
                <a href={`tel:${brand.phoneTel}`} className="font-semibold text-navy underline">
                  {brand.phone}
                </a>
                .
              </p>
            </div>
            <div>
              <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.15em] mb-2">
                Síganos
              </div>
              <SocialLinks variant="contact" />
            </div>
          </Reveal>

          <div>
            <div className="rounded-lg border border-border bg-gradient-navy text-white p-4 lg:p-6 shadow-md">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-2">
                Envíenos un mensaje
              </p>
              <h2 className="font-heading text-2xl lg:text-3xl font-bold mb-4 leading-tight">
                Solicite su revisión de caso gratis.
              </h2>
              <div className="rounded">
                <CaseReviewForm language="es" tone="dark" />
              </div>

            </div>

            <p className="mt-6 text-sm text-muted-foreground leading-relaxed">
              ¿Prefiere hablar ahora? Llame al{" "}
              <a href={`tel:${brand.phoneTel}`} className="font-semibold text-navy underline">
                {brand.phone}
              </a>{" "}
              — contestará un CPA. Para asuntos urgentes (avisos del IRS, embargos, gravámenes),
              por favor llame.
            </p>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

const InfoRow = ({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  href?: string;
}) => (
  <div className="flex items-start gap-3">
    <Icon className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
    <div>
      <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.15em] mb-1">
        {label}
      </div>
      {href ? (
        <a href={href} className="font-semibold text-navy hover:text-gold break-all">
          {value}
        </a>
      ) : (
        <div className="font-semibold text-navy">{value}</div>
      )}
    </div>
  </div>
);

export default Ayuda;
